import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import DashLayout from "@/components/DashLayout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, MessageSquare, Calendar, Sparkles, ArrowRight, Filter, Search, ArrowUp, ArrowDown, Users, Video, UserPlus, X, Trash2, RefreshCw, History, Phone, Mail, User as UserIcon, Plus, GripVertical, ClipboardList } from "lucide-react";
import DailyQuote from "@/components/DailyQuote";
import { Skeleton } from "@/components/Skeleton";
import ContactTimeline from "@/components/ContactTimeline";
import PatientQuickView from "@/components/PatientQuickView";
import FollowUpQueue from "@/components/FollowUpQueue";
import SortableGrid from "@/components/SortableGrid";
import { useDashboardLayout } from "@/hooks/useDashboardLayout";
import { useEditMode } from "@/lib/editMode";

const riskStyle = {
  green: "text-green-500 bg-green-500/10 border-green-500/30",
  amber: "text-amber-500 bg-amber-500/10 border-amber-500/30",
  red: "text-red-500 bg-red-500/10 border-red-500/30",
};

// Batch 6A · Compact active-Case summary for caseload cards/rows.
// e.g. "2 active cases · ACL · Shoulder" → graceful "+N" overflow.
function caseSummary(cases) {
  const list = (cases || []).filter(Boolean);
  if (!list.length) return null;
  const shown = list.slice(0, 2);
  const extra = list.length - shown.length;
  const noun = list.length === 1 ? "active case" : "active cases";
  return `${list.length} ${noun} · ${shown.join(" · ")}${extra > 0 ? ` · +${extra}` : ""}`;
}

// Batch 8A · Compact derived Needs-Attention reason line (clinician-facing only).
// Reasons come from the backend in priority order with canonical `flags`.
// Batch 8F · each reason deep-links to the most relevant existing workflow:
//   HIGH_CLINICAL_RISK → Patient Profile Overview
//   REVIEW_DUE         → Patient Profile · Cases & Programmes
//   PROGRESS_CONCERN   → Patient Profile Overview (Batch 9C · patient-level, no Case guess)
//   CANCELLED_NO_REBOOK→ Patient Profile · Appointments
//   UNREAD_MESSAGE     → exact patient conversation in Messages
// Each link stops propagation so the card's own quick-view click is unaffected.
const ATTENTION_LINKS = {
  HIGH_CLINICAL_RISK: { label: "High clinical risk", to: (p) => `/clinician/patient/${p.id}?tab=overview` },
  REVIEW_DUE: { label: "Review due", to: (p) => {
    const cs = p.attention?.review_due_cases || [];
    // Single overdue Case → deep-link straight to it; multiple → Cases tab only (no guessing).
    return cs.length === 1 ? `/clinician/patient/${p.id}?tab=cases&case=${cs[0].case_id}` : `/clinician/patient/${p.id}?tab=cases`;
  } },
  // Batch 9C · patient-level triage signal — never Case-attributed, so it opens
  // the patient Overview. The clinician-safe detail (e.g. "Repeated high-risk
  // Check-Ins") is shown as the link tooltip; no algorithm maths is exposed.
  PROGRESS_CONCERN: {
    label: "Progress concern",
    to: (p) => `/clinician/patient/${p.id}?tab=overview`,
    tip: (p) => p.attention?.progress_concern_detail || "Recent Check-Ins warrant review",
  },
  CANCELLED_NO_REBOOK: { label: "Cancelled · no rebooking", to: (p) => `/clinician/patient/${p.id}?tab=appointments` },
  UNREAD_MESSAGE: { label: null, to: (p) => `/clinician/messages?to=${p.id}` },
};

function AttentionReasons({ p }) {
  const a = p.attention;
  if (!a || !(a.flags || []).length) return null;
  const label = a.priority === "urgent" ? "Urgent" : "Needs attention";
  const color = a.priority === "urgent" ? "#EF4444" : "#f8a766";
  const unreadLabel = p.unread > 0 ? `${p.unread} unread` : null;
  return (
    <div className="mt-1 flex items-center gap-1 text-[11px] flex-wrap" data-testid={`attention-${p.id}`} style={{ color }}>
      <AlertTriangle size={11} className="shrink-0" />
      <span className="font-semibold uppercase tracking-wider">{label}</span>
      {(a.flags || []).map((flag) => {
        const def = ATTENTION_LINKS[flag];
        if (!def) return null;
        const text = flag === "UNREAD_MESSAGE" ? unreadLabel : def.label;
        if (!text) return null;
        return (
          <Link
            key={flag}
            to={def.to(p)}
            onClick={(e) => e.stopPropagation()}
            className="underline underline-offset-2 hover:text-[#f47a20]"
            data-testid={flag === "UNREAD_MESSAGE" ? `attention-unread-link-${p.id}` : `attention-link-${flag.toLowerCase()}-${p.id}`}
            title={def.tip ? def.tip(p) : "Open the related workflow"}
          >
            · {text}
          </Link>
        );
      })}
    </div>
  );
}

export default function ClinicianCaseload() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  // iter101.2 · Rearrangeable widgets — one editLayout flag drives both the
  // 6-stat row and the CPD/library launcher strip.
  const { editing: editLayout } = useEditMode();
  const {
    visibleOrder: statOrder,
    setOrder: setStatOrder,
    toggleHidden: hideStat,
    resetToDefault: resetStats,
    hidden: statHidden,
    isDirty: statsDirty,
  } = useDashboardLayout("clinician_home_stats", ["total", "green", "amber", "red", "unread", "next24h"]);
  const {
    visibleOrder: launcherOrder,
    setOrder: setLauncherOrder,
    toggleHidden: hideLauncher,
    resetToDefault: resetLauncher,
    hidden: launcherHidden,
    isDirty: launcherDirty,
  } = useDashboardLayout("clinician_home_launcher", ["libraries", "cpd"]);
  const [patients, setPatients] = useState([]);
  const [filter, setFilter] = useState("all");
  const [scope, setScope] = useState("mine");   // "mine" | "clinic"
  const [clinicianFilter, setClinicianFilter] = useState(""); // "" = all, or clinician id
  const [q, setQ] = useState("");
  const [view, setView] = useState("table"); // compact table only (card layout retired — Phase 1B)
  const [sort, setSort] = useState({ key: "risk", dir: "asc" });
  const [showAdd, setShowAdd] = useState(false);
  const [showCpdAdd, setShowCpdAdd] = useState(false);
  const [cpdDraft, setCpdDraft] = useState({ provider: "", login_url: "", notes: "" });
  const [cpdSaving, setCpdSaving] = useState(false);
  const [team, setTeam] = useState([]);
  const [newClin, setNewClin] = useState({ name: "", email: "", password: "", title: "", specialities: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [caseloadLoading, setCaseloadLoading] = useState(true);
  const [timelineFor, setTimelineFor] = useState(null); // {id, name} or null
  const [quickViewFor, setQuickViewFor] = useState(null); // patient id or null — opens PatientQuickView modal (Quick Contact)
  // Req 3B · In "My caseload", default the clinician filter to the signed-in clinician.
  useEffect(() => {
    if (user?.role === "clinician" && user?.id) setClinicianFilter((cf) => cf || user.id);
  }, [user]);
  // Batch 10A.1 · pending AI proposal count — reuses the EXISTING
  // /notifications/counts endpoint (unread `proposal_pending` notifications,
  // already scoped to this clinician). No new counter/endpoint/poll.
  const [pendingProposals, setPendingProposals] = useState(0);
  const loadProposalCount = () => api.get("/notifications/counts")
    .then((r) => setPendingProposals(r.data?.proposals || 0))
    .catch(() => {});

  // Phase 1B-2.1 · Pending draft exercise counter (clinical admins only).
  // Reuses the existing exercise governance — no separate review system.
  const isClinicalAdmin = (user?.role === "admin" || !!user?.is_admin) && !!user?.clinical_admin;
  const [pendingExercises, setPendingExercises] = useState({ total: 0, eligible: 0 });
  const loadPendingExercises = () => api.get("/exercises/pending-count")
    .then((r) => setPendingExercises({ total: r.data?.total || 0, eligible: r.data?.eligible || 0 }))
    .catch(() => {});

  const loadTeam = () => api.get("/clinicians").then((r) => setTeam(r.data)).catch(() => {});
  const loadCaseload = () => api.get("/caseload")
    .then((r) => setPatients(r.data))
    .catch(() => {})
    .finally(() => setCaseloadLoading(false));

  const markContacted = async (e, patient) => {
    e.preventDefault(); e.stopPropagation();
    const prev = patient.contact_status;
    // Optimistic
    setPatients((list) => list.map((x) => x.id === patient.id
      ? { ...x, contact_status: "contacted", contacted_by_name: user?.name, first_contact_at: x.first_contact_at || new Date().toISOString() }
      : x));
    try {
      const r = await api.post(`/clinician/patients/${patient.id}/mark-contacted`);
      const first = r.data?.first_contact_at || null;
      setPatients((list) => list.map((x) => x.id === patient.id ? { ...x, first_contact_at: first || x.first_contact_at } : x));
      toast.success(`Marked ${patient.name} as contacted`, {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post(`/clinician/patients/${patient.id}/mark-new`);
              setPatients((list) => list.map((x) => x.id === patient.id
                ? { ...x, contact_status: "new", contacted_by_name: null }
                : x));
              toast("Restored NEW flag");
            } catch { toast.error("Undo failed"); }
          },
        },
        duration: 5000,
      });
    } catch {
      // Rollback
      setPatients((list) => list.map((x) => x.id === patient.id ? { ...x, contact_status: prev } : x));
      toast.error("Could not update — try again");
    }
  };
  const [providers, setProviders] = useState([]);
  const [cpdAccounts, setCpdAccounts] = useState([]);
  const [refreshingList, setRefreshingList] = useState(false);
  useEffect(() => {
    loadCaseload();
    loadTeam();
    loadProposalCount();
    loadPendingExercises();
    api.get("/exercise-providers").then((r) => setProviders(r.data)).catch(() => {});
    api.get("/cpd-accounts").then((r) => setCpdAccounts(r.data)).catch(() => {});
  }, []);

  const refreshPatientList = async () => {
    setRefreshingList(true);
    try {
      await Promise.all([loadCaseload(), loadTeam()]);
      toast.success("Patient list refreshed");
    } catch { toast.error("Refresh failed"); }
    finally { setRefreshingList(false); }
  };

  const addClinician = async () => {
    if (!newClin.name || !newClin.email || !newClin.password) {
      toast.error("Name, email and password are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/clinicians", {
        name: newClin.name,
        email: newClin.email,
        password: newClin.password,
        title: newClin.title || null,
        specialities: newClin.specialities ? newClin.specialities.split(",").map((s) => s.trim()).filter(Boolean) : [],
        phone: newClin.phone || null,
      });
      toast.success(`${newClin.name} added to the team`);
      setNewClin({ name: "", email: "", password: "", title: "", specialities: "", phone: "" });
      setShowAdd(false);
      loadTeam();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add clinician");
    }
    setSaving(false);
  };

  const removeClinician = async (c) => {
    if (!window.confirm(`Remove ${c.name} from the team?\nHistorical records and past appointments are preserved.`)) return;
    setRemoving(c.id);
    try {
      const { data } = await api.delete(`/clinicians/${c.id}`);
      const suffix = data?.upcoming_appointments ? ` (${data.upcoming_appointments} upcoming appointment${data.upcoming_appointments === 1 ? "" : "s"} still assigned — please reassign)` : "";
      toast.success(`${c.name} removed from the team${suffix}`);
      loadTeam();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove clinician");
    }
    setRemoving(null);
  };

  const filtered = useMemo(() => {
    // Scope: "mine" = patients assigned to the current clinician (or admin sees everyone)
    let arr = patients;
    if (scope === "mine" && user?.id && user?.role !== "admin") {
      arr = arr.filter((p) => p.assigned_clinician_id === user.id);
    }
    // Explicit clinician filter (chip in filter bar OR click on a clinician summary box)
    if (clinicianFilter) {
      arr = arr.filter((p) => p.assigned_clinician_id === clinicianFilter);
    }
    if (filter === "attention") {
      arr = arr.filter((p) => (p.attention?.flags?.length || 0) > 0);
    } else {
      arr = arr.filter((p) => filter === "all" || p.risk === filter);
    }
    if (q) {
      const s = q.toLowerCase();
      arr = arr.filter((p) => p.name?.toLowerCase().includes(s) || p.email?.toLowerCase().includes(s) || p.sport?.toLowerCase().includes(s));
    }
    const riskOrder = { red: 0, amber: 1, green: 2 };
    arr.sort((a, b) => {
      let v = 0;
      if (sort.key === "risk") v = riskOrder[a.risk] - riskOrder[b.risk];
      if (sort.key === "adherence") v = a.adherence - b.adherence;
      if (sort.key === "name") v = (a.name || "").localeCompare(b.name || "");
      if (sort.key === "unread") v = (b.unread || 0) - (a.unread || 0);
      return sort.dir === "asc" ? v : -v;
    });
    // Needs Attention view: order by attention priority (urgent → review →
    // follow-up → message). Stable sort preserves the existing caseload order
    // within the same priority (no second scoring system).
    if (filter === "attention") {
      arr = [...arr].sort((a, b) => (a.attention?.rank ?? 99) - (b.attention?.rank ?? 99));
    }
    return arr;
  }, [patients, filter, q, sort, scope, user, clinicianFilter]);

  // Count of patients with one or more attention flags in the current scope
  // (ignores the risk/attention filter itself so the chip badge stays stable).
  const attentionCount = useMemo(() => {
    let arr = patients;
    if (scope === "mine" && user?.id && user?.role !== "admin") arr = arr.filter((p) => p.assigned_clinician_id === user.id);
    if (clinicianFilter) arr = arr.filter((p) => p.assigned_clinician_id === clinicianFilter);
    return arr.filter((p) => (p.attention?.flags?.length || 0) > 0).length;
  }, [patients, scope, user, clinicianFilter]);

  // Per-clinician summary counts (across the "scope"-filtered patient pool, before the clinician filter is applied).
  const perClinician = useMemo(() => {
    const scoped = (scope === "mine" && user?.id && user?.role !== "admin")
      ? patients.filter((p) => p.assigned_clinician_id === user.id)
      : patients;
    const counts = new Map();
    for (const p of scoped) {
      if (!p.assigned_clinician_id) continue; // shared-clinic model: every patient is auto-assigned; skip stragglers.
      const key = p.assigned_clinician_id;
      const cur = counts.get(key) || { id: key, name: p.assigned_clinician_name || "", total: 0, red: 0, amber: 0, green: 0 };
      cur.total += 1;
      cur[p.risk] = (cur[p.risk] || 0) + 1;
      counts.set(key, cur);
    }
    // Ensure every team clinician appears even with zero patients so they can still be selected.
    for (const c of team) {
      if (!counts.has(c.id)) counts.set(c.id, { id: c.id, name: c.name, total: 0, red: 0, amber: 0, green: 0 });
    }
    return Array.from(counts.values()).sort((a, b) => b.total - a.total || (a.name || "").localeCompare(b.name || ""));
  }, [patients, scope, user, team]);

  const stats = {
    total: filtered.length,
    green: filtered.filter((p) => p.risk === "green").length,
    amber: filtered.filter((p) => p.risk === "amber").length,
    red: filtered.filter((p) => p.risk === "red").length,
    unread: filtered.reduce((s, p) => s + (p.unread || 0), 0),
    next24h: filtered.filter((p) => p.next_appointment && new Date(p.next_appointment.scheduled_at) - Date.now() < 86400000).length,
  };

  return (
    <DashLayout>
      <div className="mb-6 flex items-start justify-end gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          {isClinicalAdmin && pendingExercises.total > 0 && (
            <Link
              to="/clinician/library?status=pending"
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full transition-colors"
              style={{ background: "color-mix(in oklab, #F59E0B 14%, transparent)",
                       color: "#FCD34D",
                       border: "1px solid color-mix(in oklab, #F59E0B 40%, transparent)" }}
              title={pendingExercises.eligible !== pendingExercises.total
                ? `${pendingExercises.total} pending · ${pendingExercises.eligible} you can review`
                : "Review pending draft exercises"}
              data-testid="pending-exercises-badge">
              <ClipboardList size={13} /> Pending reviews · {pendingExercises.total}
              {pendingExercises.eligible !== pendingExercises.total && (
                <span className="opacity-80">({pendingExercises.eligible} to review)</span>
              )}
            </Link>
          )}
          {pendingProposals > 0 && (
            <Link
              to="/clinician/monitoring"
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full transition-colors"
              style={{ background: "color-mix(in oklab, var(--fl-orange) 14%, transparent)",
                       color: "var(--fl-orange)",
                       border: "1px solid color-mix(in oklab, var(--fl-orange) 40%, transparent)" }}
              title="Review pending AI Recovery Coach proposals"
              data-testid="pending-proposals-badge">
              <Sparkles size={13} /> AI proposals · {pendingProposals}
            </Link>
          )}
          <button className="fl-btn" onClick={() => setShowAdd(true)} data-testid="add-clinician-btn">
            <UserPlus size={16} /> Add clinician
          </button>
        </div>
      </div>

      {/* Add my-own CPD account dialog */}
      {showCpdAdd && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
             onClick={() => setShowCpdAdd(false)}>
          <div className="fl-card p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="cpd-add-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="fl-label text-[#f47a20]">Personal</div>
                <div className="fl-heading text-xl mt-1">Save a CPD login</div>
              </div>
              <button onClick={() => setShowCpdAdd(false)} className="text-zinc-400 hover:text-white" aria-label="Close"><X size={18}/></button>
            </div>
            <div className="text-xs mb-4" style={{ color: "var(--fl-muted-2)" }}>
              Only you will see this account. Your teammates keep their own list.
            </div>
            <div className="space-y-3">
              <div>
                <div className="fl-label mb-1">Provider name</div>
                <input className="fl-input" value={cpdDraft.provider}
                       onChange={(e) => setCpdDraft({ ...cpdDraft, provider: e.target.value })}
                       placeholder="e.g. Osteopathic Journal Club" data-testid="cpd-add-provider" />
              </div>
              <div>
                <div className="fl-label mb-1">Login URL</div>
                <input className="fl-input" value={cpdDraft.login_url}
                       onChange={(e) => setCpdDraft({ ...cpdDraft, login_url: e.target.value })}
                       placeholder="https://…/login" data-testid="cpd-add-url" />
              </div>
              <div>
                <div className="fl-label mb-1">Notes (optional)</div>
                <input className="fl-input" value={cpdDraft.notes}
                       onChange={(e) => setCpdDraft({ ...cpdDraft, notes: e.target.value })}
                       placeholder="Renewal date, subscription tier, etc." data-testid="cpd-add-notes" />
              </div>
            </div>
            <div className="mt-6 flex gap-2">
              <button
                onClick={async () => {
                  const p = cpdDraft.provider.trim(); const u = cpdDraft.login_url.trim();
                  if (!p || !u) return toast.error("Provider and URL are required");
                  if (!/^https?:\/\//i.test(u)) return toast.error("URL must start with http(s)://");
                  setCpdSaving(true);
                  try {
                    const r = await api.post("/cpd-accounts",
                      { provider: p, login_url: u, notes: cpdDraft.notes || null });
                    setCpdAccounts((s) => [...s, r.data].sort((a, b) => a.provider.localeCompare(b.provider)));
                    setShowCpdAdd(false);
                    toast.success("Saved to your CPD list");
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Save failed");
                  } finally { setCpdSaving(false); }
                }}
                disabled={cpdSaving}
                className="fl-btn flex-1 justify-center" data-testid="cpd-add-save">
                <Plus size={14}/> {cpdSaving ? "Saving…" : "Save"}
              </button>
              <button onClick={() => setShowCpdAdd(false)} className="fl-btn-ghost">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Add clinician dialog */}
      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>          <div className="fl-card p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="fl-label text-[#f47a20]">New team member</div>
                <div className="fl-heading text-xl mt-1">Add clinician</div>
              </div>
              <button onClick={() => setShowAdd(false)} className="text-zinc-400 hover:text-white" data-testid="add-clinician-close"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="fl-label mb-1">Full name</div>
                <input className="fl-input" value={newClin.name} onChange={(e) => setNewClin({ ...newClin, name: e.target.value })} placeholder="Andrea Rossi" data-testid="new-clin-name" />
              </div>
              <div>
                <div className="fl-label mb-1">Job title</div>
                <input className="fl-input" value={newClin.title} onChange={(e) => setNewClin({ ...newClin, title: e.target.value })} placeholder="Lead Sport therapist" data-testid="new-clin-title" />
              </div>
              <div>
                <div className="fl-label mb-1">Email</div>
                <input className="fl-input" type="email" value={newClin.email} onChange={(e) => setNewClin({ ...newClin, email: e.target.value })} placeholder="andrea@functionallab.com" data-testid="new-clin-email" />
              </div>
              <div>
                <div className="fl-label mb-1">Password</div>
                <input className="fl-input" type="text" value={newClin.password} onChange={(e) => setNewClin({ ...newClin, password: e.target.value })} placeholder="Set an initial password" data-testid="new-clin-password" />
              </div>
              <div>
                <div className="fl-label mb-1">Phone (optional)</div>
                <input className="fl-input" value={newClin.phone} onChange={(e) => setNewClin({ ...newClin, phone: e.target.value })} placeholder="+44 20 …" data-testid="new-clin-phone" />
              </div>
              <div>
                <div className="fl-label mb-1">Specialities (comma-separated)</div>
                <input className="fl-input" value={newClin.specialities} onChange={(e) => setNewClin({ ...newClin, specialities: e.target.value })} placeholder="Sports injuries, RTS" data-testid="new-clin-specialities" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button className="fl-btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="fl-btn" onClick={addClinician} disabled={saving} data-testid="new-clin-save">
                {saving ? "Adding…" : "Add clinician"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scope toggle: My caseload vs Entire clinic */}
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <div className="fl-label text-[10px] mr-1">Scope:</div>
        <button
          onClick={() => { setScope("mine"); if (user?.role === "clinician" && user?.id) setClinicianFilter(user.id); }}
          className={`px-3 py-1.5 rounded text-xs uppercase tracking-wider border transition-colors ${scope === "mine" ? "text-[color:var(--fl-orange)]" : ""}`}
          style={{ borderColor: scope === "mine" ? "var(--fl-orange)" : "var(--fl-border)", color: scope === "mine" ? "var(--fl-orange)" : "var(--fl-muted)" }}
          data-testid="scope-mine"
        >
          My caseload
        </button>
        <button
          onClick={() => { setScope("clinic"); setClinicianFilter(""); }}
          className={`px-3 py-1.5 rounded text-xs uppercase tracking-wider border transition-colors ${scope === "clinic" ? "text-[color:var(--fl-orange)]" : ""}`}
          style={{ borderColor: scope === "clinic" ? "var(--fl-orange)" : "var(--fl-border)", color: scope === "clinic" ? "var(--fl-orange)" : "var(--fl-muted)" }}
          data-testid="scope-clinic"
        >
          Entire clinic
        </button>
        {user?.name && (
          <span className="text-xs ml-3" style={{ color: "var(--fl-muted-2)" }}>
            {scope === "mine" ? "Filtered to " : "Viewing whole clinic — you: "}
            <span className="font-semibold" style={{ color: "var(--fl-text)" }}>{user.name}</span>
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-3 items-center mb-6">
        <div className="flex gap-2">
          {["all", "red", "amber", "green"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={`px-3 py-1.5 rounded text-xs uppercase tracking-wider border ${filter === f ? "border-[#f47a20] text-[#f47a20]" : "border-white/10 text-zinc-400"}`}>
              {f}
            </button>
          ))}
          <button
            onClick={() => setFilter("attention")}
            data-testid="filter-attention"
            className={`px-3 py-1.5 rounded text-xs uppercase tracking-wider border flex items-center gap-1 ${filter === "attention" ? "border-[#f47a20] text-[#f47a20]" : "border-white/10 text-zinc-400"}`}
            title="Patients who may need a clinical follow-up action"
          >
            <AlertTriangle size={11} /> Needs attention{attentionCount > 0 ? ` (${attentionCount})` : ""}
          </button>
        </div>
        <div className="flex items-center gap-1">
          <select
            className="fl-input text-xs py-1.5"
            value={clinicianFilter}
            onChange={(e) => setClinicianFilter(e.target.value)}
            data-testid="clinician-filter-select"
          >
            <option value="">All clinicians</option>
            {team.map((c) => (
              <option key={c.id} value={c.id}>{c.name}{c.id === user?.id ? " (you)" : ""}</option>
            ))}
          </select>
          {clinicianFilter && (
            <button
              onClick={() => setClinicianFilter("")}
              className="text-zinc-500 hover:text-[color:var(--fl-orange)] px-1.5"
              title="Reset clinician filter"
              data-testid="clinician-filter-clear"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="flex-1 relative min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input className="fl-input pl-9" placeholder="Search name, email, sport…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="case-search" />
        </div>
        <button
          onClick={refreshPatientList}
          className="fl-btn-ghost text-xs"
          title="Reload patient list"
          data-testid="case-refresh"
        >
          <RefreshCw size={13} className={refreshingList ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {caseloadLoading && patients.length === 0 ? (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="caseload-loading">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="fl-card fl-card-elevated p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <Skeleton className="h-4 w-32 rounded-md" />
                  <Skeleton className="h-2.5 w-24 mt-2 rounded" />
                  <Skeleton className="h-2 w-40 mt-2 rounded" />
                </div>
                <Skeleton className="h-4 w-12 rounded" />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div>
                  <Skeleton className="h-2 w-16 rounded" />
                  <Skeleton className="h-6 w-14 mt-2 rounded-md" />
                  <Skeleton className="h-1 w-full mt-2 rounded" />
                </div>
                <div>
                  <Skeleton className="h-2 w-16 rounded" />
                  <Skeleton className="h-3 w-24 mt-2 rounded" />
                  <Skeleton className="h-2 w-14 mt-1 rounded" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : view === "cards" ? (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((p) => {
            const isNew = p.contact_status === "new";
            return (
            <div
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/clinician/patient/${p.id}`)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && navigate(`/clinician/patient/${p.id}`)}
              key={p.id}
              className="fl-card p-5 hover:border-[#f47a20]/50 transition-colors relative text-left w-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#f47a20]/40"
              style={isNew ? { boxShadow: "inset 0 0 0 2px #EF4444" } : undefined}
              data-testid={`case-card-${p.id}`}
            >
              {isNew && (
                <button
                  type="button"
                  onClick={(e) => markContacted(e, p)}
                  className="absolute -top-2 -right-2 text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider cursor-pointer hover:brightness-110 active:scale-95 transition"
                  style={{ background: "#EF4444", color: "white", boxShadow: "0 4px 12px -2px rgba(239,68,68,0.5)" }}
                  data-testid={`case-new-badge-${p.id}`}
                  title="Click to mark as contacted"
                >
                  NEW · to contact
                </button>
              )}
              {!isNew && p.contacted_by_name && p.first_contact_at && (
                <div className="absolute -top-2 -right-2 text-[9px] px-2 py-0.5 rounded-full text-zinc-400 bg-white/5 border border-white/10"
                     data-testid={`case-contacted-by-${p.id}`}
                     title={`First contacted ${new Date(p.first_contact_at).toLocaleString("en-GB")}`}>
                  Contacted · {p.contacted_by_name.split(" ")[0]}
                </div>
              )}
              <div className="flex items-start justify-between">
                <div>
                  <div className="fl-heading text-lg">{p.name}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{p.sport || "—"} · {p.main_goal || "no goal set"}</div>
                  {caseSummary(p.active_cases) && (
                    <div className="text-[11px] text-[#f8a766] mt-1 truncate" data-testid={`case-summary-${p.id}`} title={(p.active_cases || []).join(" · ")}>
                      {caseSummary(p.active_cases)}
                    </div>
                  )}
                  <AttentionReasons p={p} />
                  <div className="text-[10px] text-zinc-500 mt-0.5" data-testid={`case-clinician-${p.id}`}>
                    <Users size={9} className="inline mr-1" /> {p.assigned_clinician_name || "—"}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setQuickViewFor(p.id); }}
                    onKeyDown={(e) => e.stopPropagation()}
                    className="text-zinc-400 hover:text-[#f47a20] p-1 rounded"
                    title="Quick contact"
                    aria-label="Quick contact"
                    data-testid={`case-quick-contact-${p.id}`}
                  >
                    <Phone size={13} />
                  </button>
                  <span className={`text-[10px] px-2 py-0.5 rounded border uppercase ${riskStyle[p.risk]}`}>{p.risk}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div>
                  <div className="fl-label">Adherence 7d</div>
                  {p.has_schedule === false ? (
                    <div className="text-sm text-zinc-500 mt-1" data-testid={`adherence-card-${p.id}`}>No data</div>
                  ) : (
                    <>
                      <div className="fl-heading text-2xl text-[#f47a20]" data-testid={`adherence-card-${p.id}`}>{p.adherence}%</div>
                      <div className="h-1 bg-white/10 rounded mt-1 overflow-hidden"><div className="h-full bg-[#f47a20]" style={{ width: `${p.adherence}%` }} /></div>
                    </>
                  )}
                </div>
                <div>
                  <div className="fl-label">Last check-in</div>
                  {p.last_checkin ? (
                    <>
                      <div className="text-sm">Pain {p.last_checkin.pain} · fatigue {p.last_checkin.fatigue}</div>
                      <div className="text-[10px] text-zinc-500">{new Date(p.last_checkin.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</div>
                    </>
                  ) : <div className="text-sm text-zinc-500">—</div>}
                </div>
              </div>
              <div className="mt-4 flex justify-between items-center text-xs text-zinc-400">
                <div className="flex items-center gap-1">
                  <Calendar size={12} className="text-[#f47a20]" />
                  {p.next_appointment
                    ? new Date(p.next_appointment.scheduled_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
                    : "No upcoming appt"}
                </div>
                {p.unread > 0 && (
                  <div className="flex items-center gap-1 text-[#f47a20]"><MessageSquare size={12} /> {p.unread}</div>
                )}
              </div>
              {(p.last_touchpoint || p.unread > 0) && (
                <div className="mt-2 flex items-center justify-between text-[10px] text-zinc-500 pt-2 border-t border-white/5">
                  <div className="truncate" data-testid={`case-last-touch-${p.id}`}>
                    {p.last_touchpoint ? (
                      <>Last touch · {new Date(p.last_touchpoint.at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} · {(p.last_touchpoint.type || "contact").replace("_", " ")}</>
                    ) : "—"}
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTimelineFor({ id: p.id, name: p.name }); }}
                    className="text-zinc-400 hover:text-[#f47a20] flex items-center gap-1 shrink-0"
                    title="Open contact timeline"
                    data-testid={`case-timeline-btn-${p.id}`}
                  >
                    <History size={10} /> Timeline
                  </button>
                </div>
              )}
            </div>
          );})}
        </div>
      ) : (
        <div className="fl-card overflow-hidden">
          <div className="grid grid-cols-12 gap-3 p-3 border-b border-white/10 fl-label">
            <div className="col-span-3">Patient</div>
            <div className="col-span-2">Clinician</div>
            <div className="col-span-2">Adherence</div>
            <div className="col-span-2">Last check-in</div>
            <div className="col-span-2">Next appt</div>
            <div className="col-span-1">Risk</div>
          </div>
          {filtered.map((p) => (
            <div
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/clinician/patient/${p.id}`)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && navigate(`/clinician/patient/${p.id}`)}
              key={p.id}
              className="grid grid-cols-12 gap-3 p-3 border-b border-white/5 hover:bg-white/5 items-center text-sm w-full text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#f47a20]/40"
              data-testid={`case-row-${p.id}`}
              style={p.contact_status === "new" ? { boxShadow: "inset 3px 0 0 0 #EF4444" } : undefined}>
              <div className="col-span-3">
                <div className="flex items-center gap-2">
                  <span>{p.name}</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setQuickViewFor(p.id); }}
                    onKeyDown={(e) => e.stopPropagation()}
                    className="text-zinc-400 hover:text-[#f47a20] p-0.5 rounded shrink-0"
                    title="Quick contact"
                    aria-label="Quick contact"
                    data-testid={`case-row-quick-contact-${p.id}`}
                  >
                    <Phone size={12} />
                  </button>
                  {p.contact_status === "new" && (
                    <button
                      type="button"
                      onClick={(e) => markContacted(e, p)}
                      className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wider cursor-pointer hover:brightness-110 active:scale-95 transition"
                      style={{ background: "#EF4444", color: "white" }}
                      data-testid={`case-row-new-badge-${p.id}`}
                      title="Click to mark as contacted"
                    >
                      NEW
                    </button>
                  )}
                </div>
                <div className="text-xs text-zinc-500">{p.email}</div>
                {caseSummary(p.active_cases) && (
                  <div className="text-[11px] text-[#f8a766] truncate" data-testid={`case-summary-row-${p.id}`} title={(p.active_cases || []).join(" · ")}>
                    {caseSummary(p.active_cases)}
                  </div>
                )}
                <AttentionReasons p={p} />
                {p.unread > 0 && (
                  <div className="text-[11px] text-[#f47a20] flex items-center gap-1 mt-0.5" data-testid={`case-row-unread-${p.id}`}>
                    <MessageSquare size={11} /> {p.unread} unread
                  </div>
                )}
              </div>
              <div className="col-span-2 text-xs" data-testid={`case-row-clinician-${p.id}`}>
                <span style={{ color: p.assigned_clinician_name ? "var(--fl-text-2)" : "var(--fl-muted-2)" }}>{p.assigned_clinician_name || "—"}</span>
              </div>
              <div className="col-span-2" data-testid={`adherence-row-${p.id}`}>
                {p.has_schedule === false ? (
                  <span className="text-zinc-500">No data</span>
                ) : (
                  <>{p.adherence}% <div className="h-1 bg-white/10 rounded overflow-hidden mt-1"><div className="h-full bg-[#f47a20]" style={{ width: `${p.adherence}%` }} /></div></>
                )}
              </div>
              <div className="col-span-2 text-xs text-zinc-500">
                {p.last_checkin ? `pain ${p.last_checkin.pain}` : "—"}
              </div>
              <div className="col-span-2 text-xs text-zinc-500">
                {p.next_appointment ? new Date(p.next_appointment.scheduled_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—"}
              </div>
              <div className="col-span-1">
                <span className={`text-[10px] px-2 py-0.5 rounded border uppercase ${riskStyle[p.risk]}`}>{p.risk}</span>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="p-6 text-sm text-zinc-500">No patients match your filters.</div>}
        </div>
      )}

      {quickViewFor && (
        <PatientQuickView patientId={quickViewFor} onClose={() => setQuickViewFor(null)} />
      )}

      {timelineFor && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setTimelineFor(null)}
          data-testid="timeline-modal"
        >
          <div
            className="fl-card fl-card-elevated w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setTimelineFor(null)}
              className="absolute top-3 right-3 text-zinc-500 hover:text-white"
              data-testid="timeline-modal-close"
            >
              <X size={16} />
            </button>
            <div className="mb-4">
              <div className="fl-label text-[#f47a20]">Contact history</div>
              <div className="fl-heading text-xl">{timelineFor.name}</div>
            </div>
            <ContactTimeline patientId={timelineFor.id} patientName={timelineFor.name} />
            <div className="mt-4 pt-3 border-t border-white/5 flex justify-end">
              <Link
                to={`/clinician/patient/${timelineFor.id}`}
                className="fl-btn-ghost text-xs"
                data-testid="timeline-modal-full-record"
              >
                Open full patient record <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      )}
    </DashLayout>
  );
}
